import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: '5000+', label: 'Studenți Activi' },
  { value: '100%', label: 'Gratuit' },
  { value: '24/7', label: 'Suport Online' },
];

const tickers = [
  { pair: 'EUR/USD', value: '1.0847', change: '+0.42%', up: true },
  { pair: 'GBP/USD', value: '1.2634', change: '+0.18%', up: true },
  { pair: 'USD/JPY', value: '149.82', change: '-0.23%', up: false },
  { pair: 'XAU/USD', value: '2312.5', change: '+0.67%', up: true },
  { pair: 'BTC/USD', value: '68,420', change: '+1.12%', up: true },
  { pair: 'USD/CHF', value: '0.9054', change: '-0.09%', up: false },
];

const sparkBars = [3, 5, 4, 7, 6, 8, 5, 9, 7, 10, 8, 11, 9, 12, 10];

// ─── Smooth Canvas Background ────────────────────────────────────────
function AnimatedBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    let animId: number;

    // ── Resize (track W/H in vars to avoid layout thrash) ──
    let W = 0;
    let H = 0;
    const resize = () => {
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width = W;
      canvas.height = H;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // ── Generate candle data once ──
    type Candle = { open: number; close: number; high: number; low: number };
    const CANDLE_COUNT = 40;
    const candles: Candle[] = [];
    let price = 0.5;
    for (let i = 0; i < CANDLE_COUNT + 30; i++) {
      const open = price;
      price = Math.max(0.1, Math.min(0.9, price + (Math.random() - 0.48) * 0.035));
      const close = price;
      const wick = Math.random() * 0.018;
      candles.push({ open, close, high: Math.max(open, close) + wick, low: Math.min(open, close) - wick });
    }

    // ── Generate smooth line data with extra buffer ──
    const LINE_PTS = 100;
    const lineA: number[] = [];
    const lineB: number[] = [];
    let la = 0.45, lb = 0.55;
    for (let i = 0; i < LINE_PTS + 80; i++) {
      la = Math.max(0.12, Math.min(0.88, la + (Math.random() - 0.49) * 0.022));
      lb = Math.max(0.12, Math.min(0.88, lb + (Math.random() - 0.49) * 0.018));
      lineA.push(la);
      lineB.push(lb);
    }

    // ── Particles ──
    type Particle = { x: number; y: number; r: number; vx: number; vy: number; col: string; a: number; phase: number };
    const PARTICLE_COUNT = 18;
    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      x: Math.random(),
      y: Math.random(),
      r: 1.2 + Math.random() * 2,
      vx: (Math.random() - 0.5) * 0.00025,
      vy: -0.00015 - Math.random() * 0.0003,
      col: i % 3 === 0 ? '#4ecdc4' : i % 3 === 1 ? '#c9a96e' : '#f4a29a',
      a: 0.25 + Math.random() * 0.35,
      phase: Math.random() * Math.PI * 2,
    }));

    // ── Time-based animation (frame-rate independent) ──
    let startTime = 0;

    const draw = (ts: number) => {
      if (!startTime) startTime = ts;
      const t = (ts - startTime) * 0.001; // seconds

      if (W === 0 || H === 0) {
        animId = requestAnimationFrame(draw);
        return;
      }

      // ── 1. Background (solid fill + gradient overlay — no new gradient every frame) ──
      ctx.fillStyle = '#f2ede4';
      ctx.fillRect(0, 0, W, H);

      // slow-moving radial glow — only create gradient when needed
      const gx = W * (0.5 + Math.sin(t * 0.18) * 0.28);
      const gy = H * (0.5 + Math.cos(t * 0.13) * 0.22);
      const bgGrad = ctx.createRadialGradient(gx, gy, 0, gx, gy, W * 0.7);
      bgGrad.addColorStop(0, 'rgba(210,245,242,0.9)');
      bgGrad.addColorStop(0.5, 'rgba(245,241,232,0.6)');
      bgGrad.addColorStop(1, 'rgba(250,244,234,0)');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, W, H);

      // teal glow top-right
      const tealAlpha = 0.055 + 0.03 * Math.sin(t * 0.5);
      const tg = ctx.createRadialGradient(W * 0.88, H * 0.18, 0, W * 0.88, H * 0.18, W * 0.45);
      tg.addColorStop(0, `rgba(78,205,196,${tealAlpha})`);
      tg.addColorStop(1, 'transparent');
      ctx.fillStyle = tg;
      ctx.fillRect(0, 0, W, H);

      // gold glow bottom-left
      const goldAlpha = 0.06 + 0.025 * Math.sin(t * 0.4 + 1.5);
      const gg = ctx.createRadialGradient(W * 0.08, H * 0.82, 0, W * 0.08, H * 0.82, W * 0.38);
      gg.addColorStop(0, `rgba(201,169,110,${goldAlpha})`);
      gg.addColorStop(1, 'transparent');
      ctx.fillStyle = gg;
      ctx.fillRect(0, 0, W, H);

      // ── 2. Grid — batch all lines in one path ──
      const COLS = 10;
      const ROWS = 7;
      ctx.lineWidth = 0.5;

      // vertical
      ctx.beginPath();
      for (let c = 1; c < COLS; c++) {
        const x = (c / COLS) * W;
        const a = 0.025 + 0.025 * Math.sin(t * 1.2 + c * 0.6);
        ctx.globalAlpha = a;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
      }
      ctx.strokeStyle = '#0f1419';
      ctx.stroke();

      // horizontal
      ctx.beginPath();
      for (let r = 1; r < ROWS; r++) {
        const y = (r / ROWS) * H;
        const a = 0.025 + 0.025 * Math.sin(t * 1.0 + r * 0.8);
        ctx.globalAlpha = a;
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;

      // ── 3. Candles ──
      const chartTop = H * 0.1;
      const chartH = H * 0.8;
      const candleW = W / CANDLE_COUNT;
      // continuous scroll based on time
      const candleScroll = (t * 18) % candleW;
      const candleShift = Math.floor(t * 18 / candleW);

      for (let i = 0; i < CANDLE_COUNT + 2; i++) {
        const idx = (i + candleShift) % candles.length;
        const c = candles[idx];
        const cx = i * candleW - candleScroll;
        if (cx < -candleW || cx > W + candleW) continue;

        const openY = chartTop + c.open * chartH;
        const closeY = chartTop + c.close * chartH;
        const highY = chartTop + c.high * chartH;
        const lowY = chartTop + c.low * chartH;
        const bull = c.close <= c.open;
        const bodyH = Math.abs(closeY - openY) || 1;
        const bodyY = Math.min(openY, closeY);

        const alpha = 0.45;
        const color = bull ? `rgba(78,205,196,${alpha})` : `rgba(232,96,76,${alpha})`;

        // wick
        ctx.strokeStyle = color;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(cx, highY);
        ctx.lineTo(cx, lowY);
        ctx.stroke();

        // body
        ctx.fillStyle = color;
        ctx.fillRect(cx - candleW * 0.28, bodyY, candleW * 0.56, bodyH);
      }

      // ── 4. Smooth price line A (teal) — cubic bezier ──
      const ptW = W / LINE_PTS;
      const lineScrollA = (t * 28) % ptW;
      const lineShiftA = Math.floor(t * 28 / ptW);

      ctx.save();
      // filled area
      ctx.beginPath();
      for (let i = 0; i <= LINE_PTS + 2; i++) {
        const idx = (i + lineShiftA) % lineA.length;
        const nx = (i + 1 + lineShiftA) % lineA.length;
        const x = i * ptW - lineScrollA;
        const y = chartTop + lineA[idx] * chartH;
        const cpx = x + ptW / 2;
        const cpy1 = y;
        const cpy2 = chartTop + lineA[nx] * chartH;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.bezierCurveTo(cpx, cpy1, cpx, cpy2, x + ptW, cpy2);
      }
      ctx.lineTo(W + ptW * 2, H);
      ctx.lineTo(-ptW, H);
      ctx.closePath();
      const fillA = ctx.createLinearGradient(0, chartTop, 0, chartTop + chartH);
      fillA.addColorStop(0, 'rgba(78,205,196,0.10)');
      fillA.addColorStop(1, 'rgba(78,205,196,0.00)');
      ctx.fillStyle = fillA;
      ctx.fill();

      // line itself — 2 passes (glow + crisp)
      for (let pass = 0; pass < 2; pass++) {
        ctx.beginPath();
        for (let i = 0; i <= LINE_PTS + 2; i++) {
          const idx = (i + lineShiftA) % lineA.length;
          const nx = (i + 1 + lineShiftA) % lineA.length;
          const x = i * ptW - lineScrollA;
          const y = chartTop + lineA[idx] * chartH;
          const cpx = x + ptW / 2;
          const cpy2 = chartTop + lineA[nx] * chartH;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.bezierCurveTo(cpx, y, cpx, cpy2, x + ptW, cpy2);
        }
        ctx.strokeStyle = pass === 0 ? 'rgba(78,205,196,0.18)' : 'rgba(78,205,196,0.75)';
        ctx.lineWidth = pass === 0 ? 5 : 1.5;
        ctx.stroke();
      }
      ctx.restore();

      // ── 5. Smooth price line B (gold) ──
      const lineScrollB = (t * 16) % ptW;
      const lineShiftB = Math.floor(t * 16 / ptW);

      ctx.save();
      for (let pass = 0; pass < 2; pass++) {
        ctx.beginPath();
        for (let i = 0; i <= LINE_PTS + 2; i++) {
          const idx = (i + lineShiftB + 15) % lineB.length;
          const nx = (i + 1 + lineShiftB + 15) % lineB.length;
          const x = i * ptW - lineScrollB;
          const y = chartTop + lineB[idx] * chartH * 0.55 + chartH * 0.25;
          const cpy2 = chartTop + lineB[nx] * chartH * 0.55 + chartH * 0.25;
          const cpx = x + ptW / 2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.bezierCurveTo(cpx, y, cpx, cpy2, x + ptW, cpy2);
        }
        ctx.strokeStyle = pass === 0 ? 'rgba(201,169,110,0.12)' : 'rgba(201,169,110,0.55)';
        ctx.lineWidth = pass === 0 ? 4 : 1.2;
        ctx.stroke();
      }
      ctx.restore();

      // ── 6. Particles (batched by color) ──
      const tealParticles = particles.filter(p => p.col === '#4ecdc4');
      const goldParticles = particles.filter(p => p.col === '#c9a96e');
      const pinkParticles = particles.filter(p => p.col === '#f4a29a');

      [tealParticles, goldParticles, pinkParticles].forEach(group => {
        if (!group.length) return;
        ctx.fillStyle = group[0].col;
        group.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;
          if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
          if (p.x < -0.02 || p.x > 1.02) p.x = Math.random();
          ctx.globalAlpha = p.a * (0.55 + 0.45 * Math.sin(t * 1.2 + p.phase));
          ctx.beginPath();
          ctx.arc(p.x * W, p.y * H, p.r, 0, Math.PI * 2);
          ctx.fill();
        });
      });
      ctx.globalAlpha = 1;

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block', willChange: 'contents' }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
export default function Hero() {
  const badgeRef  = useRef<HTMLDivElement>(null);
  const titleRef  = useRef<HTMLDivElement>(null);
  const descRef   = useRef<HTMLParagraphElement>(null);
  const btnsRef   = useRef<HTMLDivElement>(null);
  const statsRef  = useRef<HTMLDivElement>(null);
  const rightRef  = useRef<HTMLDivElement>(null);

  // ── Ticker: direct DOM manipulation — zero React re-renders ──
  const tickerRef   = useRef<HTMLDivElement>(null);
  const tickerPosRef = useRef(0);

  const [priceFlash, setPriceFlash] = useState(false);

  // entrance animations
  useEffect(() => {
    const delays = [
      { ref: badgeRef,  delay: 100 },
      { ref: titleRef,  delay: 300 },
      { ref: descRef,   delay: 500 },
      { ref: btnsRef,   delay: 650 },
      { ref: statsRef,  delay: 800 },
      { ref: rightRef,  delay: 400 },
    ];
    const timers = delays.map(({ ref, delay }) =>
      setTimeout(() => {
        const el = ref.current;
        if (!el) return;
        el.style.opacity = '1';
        el.style.transform = 'translateY(0) scale(1)';
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // ticker — pure rAF, no setState
  useEffect(() => {
    let animId: number;
    const TICKER_ITEM_W = 180;
    const TOTAL_W = tickers.length * TICKER_ITEM_W;
    let last = 0;

    const step = (ts: number) => {
      const dt = last ? Math.min((ts - last) * 0.06, 4) : 0;
      last = ts;
      tickerPosRef.current = (tickerPosRef.current + dt) % TOTAL_W;
      if (tickerRef.current) {
        tickerRef.current.style.transform = `translateX(-${tickerPosRef.current}px)`;
      }
      animId = requestAnimationFrame(step);
    };
    animId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animId);
  }, []);

  // price flash
  useEffect(() => {
    const iv = setInterval(() => {
      setPriceFlash(true);
      setTimeout(() => setPriceFlash(false), 300);
    }, 2500);
    return () => clearInterval(iv);
  }, []);

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-screen flex flex-col items-center overflow-hidden"
      style={{ background: '#f2ede4' }}
    >
      {/* Canvas background */}
      <AnimatedBackground />

      {/* Light frosted overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(160deg, rgba(245,241,232,0.45) 0%, rgba(245,241,232,0.18) 60%, transparent 100%)' }}
      />

      {/* ── Content ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-28 pb-12 flex-1 flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">

          {/* Left */}
          <div>
            <div
              ref={badgeRef}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium mb-6 cursor-default"
              style={{
                color: '#26a69a',
                background: 'rgba(255,255,255,0.88)',
                borderColor: 'rgba(78,205,196,0.35)',
                backdropFilter: 'blur(10px)',
                opacity: 0,
                transform: 'translateY(20px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: '#4ecdc4', animation: 'heroPulse 1.5s ease-in-out infinite' }} />
              Ghid Complet pentru Începători — 2025
            </div>

            <div
              ref={titleRef}
              style={{ opacity: 0, transform: 'translateY(28px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
            >
              <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#0f1419' }}>
                Învață Forex
                <br />
                <span style={{ color: '#4ecdc4', display: 'inline-block', animation: 'shimmerText 3s ease-in-out infinite' }}>
                  Trading
                </span>{' '}
                de la
                <br />
                Zero
              </h1>
            </div>

            <p
              ref={descRef}
              className="text-lg text-gray-700 mb-8 leading-relaxed max-w-lg"
              style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              Ghidul complet pentru începători care doresc să înțeleagă piața valutară — de la concepte de bază până la strategii avansate.
            </p>

            <div
              ref={btnsRef}
              className="flex flex-col gap-4 mb-12"
              style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              {/* Button 1 — Primary (teal gradient) */}
              <a
                href="https://clicks.affstrack.com/c?c=1197206&l=ja&p=1"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-between gap-6 px-14 py-8 rounded-3xl text-white font-semibold text-lg whitespace-nowrap cursor-pointer w-full max-w-xl"
                style={{ background: 'linear-gradient(135deg, #4ecdc4, #26a69a)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLAnchorElement).style.transform = 'scale(1.03) translateY(-2px)';
                  (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 14px 35px rgba(78,205,196,0.45)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLAnchorElement).style.transform = 'scale(1)';
                  (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20">
                    <i className="ri-user-add-line text-2xl" />
                  </div>
                  <span>Deschide Cont (Gratuit)</span>
                </div>
                <i className="ri-arrow-right-line text-xl" style={{ animation: 'arrowBounce 1.5s ease-in-out infinite' }} />
              </a>

              {/* Concepte scroll link */}
              <button
                onClick={() => scrollTo('#concepte')}
                className="flex items-center gap-2 text-sm font-medium cursor-pointer mt-1"
                style={{ color: '#666', background: 'none', border: 'none', padding: 0, transition: 'color 0.2s ease' }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#0f1419'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#666'; }}
              >
                <i className="ri-play-circle-line" />
                Vezi Conceptele de Bază
              </button>
            </div>

            <div
              ref={statsRef}
              className="flex items-center gap-10"
              style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              {stats.map((s, i) => (
                <div key={i} style={{ animation: `statPop 0.5s ease ${0.85 + i * 0.15}s both` }}>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#0f1419' }}>{s.value}</div>
                  <div className="text-sm text-gray-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div
            ref={rightRef}
            className="relative hidden lg:block"
            style={{ opacity: 0, transform: 'translateY(30px) scale(0.97)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
          >
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                aspectRatio: '4/3',
                animation: 'floatCard 6s ease-in-out infinite',
                boxShadow: '0 30px 80px rgba(15,20,25,0.15), 0 8px 30px rgba(78,205,196,0.12)',
              }}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/d3187169f16b5fc8b9c9a499624af308.jpg"
                alt="Trading Platform Dashboard"
                className="w-full h-full object-cover object-top"
              />
              <div
                className="absolute left-0 right-0 h-px pointer-events-none"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(78,205,196,0.8), transparent)', animation: 'scanLine 3.5s linear infinite' }}
              />
              <div
                className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2"
                style={{ color: '#0f1419' }}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: '#4ecdc4', animation: 'heroPulse 1s ease-in-out infinite' }} />
                Piața Live
              </div>
              <div className="absolute top-4 right-4 flex items-end gap-[2px]">
                {sparkBars.map((h, i) => (
                  <div key={i} className="w-1 rounded-sm" style={{ height: `${h * 2}px`, background: '#4ecdc4', opacity: 0.7, animation: `barGrow 1.5s ease ${i * 0.06}s both` }} />
                ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" style={{ background: 'linear-gradient(to top, rgba(15,20,25,0.88), transparent)' }}>
                <p className="font-semibold text-lg transition-colors duration-300" style={{ color: priceFlash ? '#4ecdc4' : 'white' }}>
                  EUR/USD +0.42%
                </p>
                <p className="text-gray-300 text-sm">Perechea valutară cea mai tranzacționată</p>
              </div>
            </div>

            <div
              className="absolute -bottom-6 -left-6 bg-white/92 rounded-2xl p-4 border border-white/70 cursor-default"
              style={{ backdropFilter: 'blur(14px)', animation: 'floatCardSlow 5s ease-in-out 1s infinite', boxShadow: '0 10px 40px rgba(15,20,25,0.1)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: '#4ecdc4' }}>
                  <i className="ri-shield-check-line text-white text-lg" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Risk Management</p>
                  <p className="text-sm font-bold" style={{ color: '#0f1419' }}>Capital Protejat</p>
                </div>
              </div>
            </div>

            <div
              className="absolute -top-5 -right-5 bg-white/92 rounded-2xl p-3 border border-white/70 cursor-default"
              style={{ backdropFilter: 'blur(14px)', animation: 'floatCardSlow 6s ease-in-out 0.5s infinite', boxShadow: '0 10px 40px rgba(15,20,25,0.08)' }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#fff5f3' }}>
                  <i className="ri-line-chart-line text-base" style={{ color: '#e8604c' }} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Win Rate</p>
                  <p className="text-sm font-bold" style={{ color: '#0f1419' }}>68.4%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Live Ticker Bar — pure CSS scroll ── */}
      <div
        className="relative z-10 w-full overflow-hidden border-t border-b py-3"
        style={{ borderColor: 'rgba(15,20,25,0.07)', background: 'rgba(255,255,255,0.60)', backdropFilter: 'blur(14px)' }}
      >
        {/* duplicate set for seamless loop */}
        <div ref={tickerRef} className="flex items-center will-change-transform" style={{ whiteSpace: 'nowrap' }}>
          {[...tickers, ...tickers, ...tickers, ...tickers, ...tickers].map((t, i) => (
            <div key={i} className="flex items-center gap-2 px-8">
              <span className="text-xs font-bold tracking-wide" style={{ color: '#0f1419' }}>{t.pair}</span>
              <span className="text-xs font-mono" style={{ color: '#666' }}>{t.value}</span>
              <span className="text-xs font-semibold flex items-center gap-1" style={{ color: t.up ? '#26a69a' : '#e8604c' }}>
                <i className={t.up ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                {t.change}
              </span>
              <span className="text-gray-200 ml-4 text-xs">|</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40 z-10">
        <span className="text-xs text-gray-500 tracking-widest uppercase">Scroll</span>
        <div className="w-5 h-8 rounded-full border-2 border-gray-400 flex items-start justify-center p-1">
          <div className="w-1 h-2 rounded-full bg-gray-400" style={{ animation: 'scrollDot 1.6s ease-in-out infinite' }} />
        </div>
      </div>

      <style>{`
        @keyframes heroPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(0.72); }
        }
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes floatCardSlow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes scanLine {
          0%   { top: 0%;   opacity: 0; }
          6%   { opacity: 1; }
          94%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes shimmerText {
          0%, 100% { color: #4ecdc4; }
          50% { color: #1a9e94; }
        }
        @keyframes arrowBounce {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(5px); }
        }
        @keyframes scrollDot {
          0%   { transform: translateY(0);    opacity: 1; }
          100% { transform: translateY(14px); opacity: 0; }
        }
        @keyframes statPop {
          0%   { transform: scale(0.72) translateY(10px); opacity: 0; }
          70%  { transform: scale(1.07); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes barGrow {
          0%   { transform: scaleY(0);   transform-origin: bottom; }
          100% { transform: scaleY(1);   transform-origin: bottom; }
        }
      `}</style>
    </section>
  );
}
